import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Tag, Space, Button, message, Modal, Tree, Typography, Popconfirm } from 'antd';
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import { roleApi } from '../../services/manager/role';
import { permissionApi } from '../../services/manager/permission';
import type { RoleItem } from '../../types/entity/role';
import type { PermissionItem } from '../../types/entity/permission';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../types/base.dto';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import List, { ListRef } from '../../components/Templates/List';

const { Text } = Typography;
const statusOptions = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

type PermissionTreeNode = DataNode & { id: number };

const buildPermissionTree = (items: PermissionItem[]): PermissionTreeNode[] => {
  const nodeMap = new Map<number, PermissionTreeNode>();

  items.forEach((item) => {
    nodeMap.set(item.id, {
      key: item.id,
      id: item.id,
      title: (
        <span>
          <Text strong>{item.name}</Text>
          <span style={{ color: '#999', marginLeft: 8 }}>{item.code}</span>
        </span>
      ),
      children: [],
    });
  });

  const roots: PermissionTreeNode[] = [];

  nodeMap.forEach((node, id) => {
    const permission = items.find((item) => item.id === id);
    if (permission?.parent_id && nodeMap.has(permission.parent_id)) {
      nodeMap.get(permission.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: PermissionTreeNode[]) => {
    nodes.sort((a, b) => {
      const sortA = items.find((item) => item.id === (a as PermissionTreeNode).id)?.sort ?? 0;
      const sortB = items.find((item) => item.id === (b as PermissionTreeNode).id)?.sort ?? 0;
      if (sortA !== sortB) return sortA - sortB;
      const nameA = items.find((item) => item.id === (a as PermissionTreeNode).id)?.name ?? '';
      const nameB = items.find((item) => item.id === (b as PermissionTreeNode).id)?.name ?? '';
      return nameA.localeCompare(nameB, 'zh-CN');
    });
    nodes.forEach((child) => {
      if (child.children && child.children.length > 0) {
        sortNodes(child.children as PermissionTreeNode[]);
      }
    });
  };

  sortNodes(roots);
  return roots;
};

const Roles: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; record: RoleItem | null }>({
    open: false,
    mode: 'create',
    record: null
  });

  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permissionTreeLoading, setPermissionTreeLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [checkedPermissionKeys, setCheckedPermissionKeys] = useState<number[]>([]);

  useEffect(() => {
    permissionApi.getAll().then(res => {
      if (res.code === 200 && res.data) {
        setPermissions(res.data.items);
      }
    });
  }, []);

  const permissionTree = useMemo(() => buildPermissionTree(permissions), [permissions]);

  const formFields: FormFieldConfig[] = [
    { name: 'name', label: '角色名称', type: 'text', required: true, rules: [{ required: true, message: '请输入角色名称' }, { max: 100 }], fieldProps: { placeholder: '如：后台管理员' } },
    { name: 'code', label: '角色编码', type: 'text', required: true, rules: [{ required: true, message: '请输入角色编码' }, { max: 100 }], fieldProps: { placeholder: '如：admin' }, disabled: (mode) => mode === 'edit' },
    { name: 'description', label: '描述', type: 'textarea', rules: [{ max: 255 }], fieldProps: { placeholder: '角色用途描述', rows: 3 } },
    { name: 'status', label: '状态', type: 'select', required: true, options: statusOptions, initialValue: 1 },
  ];

  const fetcher = async (params: any) => {
    const res = await roleApi.getAll();
    const items = res.data.items || [];
    
    const page = params.page || 1;
    const pageSize = params.pageSize || 20;
    const start = (page - 1) * pageSize;
    const rows = items.slice(start, start + pageSize);

    return {
      rows,
      pagination: {
        page,
        page_size: pageSize,
        total: items.length,
        total_pages: Math.ceil(items.length / pageSize)
      }
    };
  };

  const handleDelete = async (record: RoleItem) => {
    try {
      const res = await roleApi.deleteById(String(record.id));
      if (res.code !== 200) {
        throw new Error(res.message || '删除角色失败');
      }
      message.success('角色已删除');
      listRef.current?.reload();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || '删除角色失败';
      message.error(msg);
    }
  };

  const openPermissionModal = async (record: RoleItem) => {
    setSelectedRole(record);
    setPermissionTreeLoading(true);
    try {
      const permissionIds = await roleApi.getPermissions(record.id);
      setCheckedPermissionKeys(permissionIds);
      setPermModalOpen(true);
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || '获取角色权限失败';
      message.error(msg);
    } finally {
      setPermissionTreeLoading(false);
    }
  };

  const handleSaveRolePermissions = async () => {
    if (!selectedRole) return;
    try {
      await roleApi.updatePermissions(selectedRole.id, checkedPermissionKeys);
      message.success('角色权限已更新');
      setPermModalOpen(false);
      listRef.current?.reload();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || '更新角色权限失败';
      message.error(msg);
    }
  };

  const columns: ColumnsType<RoleItem> = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <span style={{ color: '#999' }}>{record.code}</span>
        </Space>
      ),
    },
    { title: '描述', dataIndex: 'description', key: 'description', render: (value?: string) => value || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number) => <Tag color={status === 1 ? 'success' : 'default'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '内置',
      dataIndex: 'built_in',
      width: 80,
      render: (builtIn: number) => (builtIn === 1 ? <Tag color="purple">系统</Tag> : <Tag color="default">自定义</Tag>),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => setModalState({ open: true, mode: 'edit', record })}>
            编辑
          </Button>
          <Button
            type="link"
            icon={<SafetyCertificateOutlined />}
            onClick={() => openPermissionModal(record)}
          >
            权限
          </Button>
          <Popconfirm
            title="确定删除该角色？"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
            disabled={record.built_in === 1}
          >
            <Button type="link" icon={<DeleteOutlined />} danger disabled={record.built_in === 1}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List<RoleItem>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>角色管理</span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => listRef.current?.reload()}>刷新</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
                新建角色
              </Button>
            </Space>
          </div>
        }
        queryFields={[]} // No search fields
        fetcher={fetcher}
        buildParams={(q) => ({ ...q })}
        columns={columns}
        rowKey={(record) => String(record.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />

      <UpsertModal<RoleItem>
        title={modalState.mode === 'create' ? '新建角色' : '编辑角色'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
        createApi={(data) => roleApi.create(data)}
        updateApi={(id, data) => roleApi.updateById(id, data)}
        formFields={formFields}
      />

      <Modal
        title={`配置角色权限${selectedRole ? ` - ${selectedRole.name}` : ''}`}
        open={permModalOpen}
        confirmLoading={permissionTreeLoading}
        onCancel={() => setPermModalOpen(false)}
        onOk={handleSaveRolePermissions}
        width={520}
        destroyOnClose
      >
        <Tree
          checkable
          selectable={false}
          fieldNames={{ title: 'title', key: 'key', children: 'children' }}
          checkedKeys={checkedPermissionKeys}
          onCheck={(checkedKeysValue) =>
            setCheckedPermissionKeys((checkedKeysValue as number[]) ?? [])
          }
          treeData={permissionTree}
          defaultExpandAll
        />
      </Modal>
    </>
  );
};

export default Roles;
